import { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { getEquipmentLabel } from '~components/Filters/useFetchers';
import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import { JobInvoicing } from '~hooks/useJob/models';
import { getDriverInfoForCards } from '~utils/drivers/driver-utils';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

import { FlexColumn } from '../../styled';

interface DriverAndTruckColumnProps {
  hideEquipment?: boolean;
  isJobEquipmentOnly?: boolean;
  job: JobInvoicing;
  sx?: SxProps<Theme>;
}

export default function DriverAndTruckColumn({
  hideEquipment = false,
  isJobEquipmentOnly = false,
  job,
  sx,
}: DriverAndTruckColumnProps) {
  return (
    <Box sx={sx} data-test-id="driver-and-truck-column">
      <DriverAndVendor isJobEquipmentOnly={isJobEquipmentOnly} job={job} />
      {!hideEquipment && <TruckAndTrailer job={job} />}
    </Box>
  );
}

interface DriverAndVendorProps {
  isJobEquipmentOnly: boolean;
  job: JobInvoicing;
}

function DriverAndVendor({ isJobEquipmentOnly, job }: DriverAndVendorProps) {
  const hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.hideDriversVendorNameIfOwnerOperator,
  });

  const { driver, vendorJobAssignment } = job;

  let content: React.ReactNode = null;
  const hasVendorOrDriver = Boolean(vendorJobAssignment?.vendorAccount?.name || driver);

  if (hasVendorOrDriver) {
    if (driver) {
      const driverInfo = getDriverInfoForCards(
        driver,
        vendorJobAssignment?.vendorAccount?.connectedCompany ?? undefined,
        hideDriversVendorNameIfOwnerOperatorFeatureFlagEnabled,
      );

      content = (
        <Box
          sx={{ display: 'flex', flex: 1, flexDirection: 'column', overflow: 'hidden' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <OverflowAwareText
              variant="body2"
              sx={(theme) => ({
                color: driverInfo.isAvailable
                  ? theme.brandV2.colors.treadBlue2
                  : theme.brandV2.colors.treadBlack,
                cursor: 'auto',
                fontWeight: 600,
                width: 'auto',
              })}
            >
              {driverInfo.equipmentName && !isJobEquipmentOnly && (
                <>
                  <Typography
                    component="span"
                    sx={(theme) => ({
                      color: theme.brandV2.colors.treadBlack,
                      fontSize: 'inherit',
                      fontWeight: 'inherit',
                    })}
                  >
                    {driverInfo.equipmentName}
                  </Typography>

                  <Typography
                    component="span"
                    sx={(theme) => ({
                      color: theme.brandV2.colors.treadBlack,
                      fontSize: 'inherit',
                      fontWeight: 'inherit',
                    })}
                  >
                    {' • '}
                  </Typography>
                </>
              )}

              <Typography
                component="span"
                sx={{ color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit' }}
              >
                {driverInfo.fullName}
              </Typography>
            </OverflowAwareText>
          </Box>

          {driverInfo.truckType && !isJobEquipmentOnly && (
            <OverflowAwareText
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray2,
                fontSize: '11px',
              })}
            >
              {driverInfo.truckType}
            </OverflowAwareText>
          )}

          {driverInfo.vendorName && (
            <OverflowAwareText
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray2,
                fontSize: '11px',
              })}
            >
              {driverInfo.vendorName}
            </OverflowAwareText>
          )}
        </Box>
      );
    } else {
      const isVendorAvailable = vendorJobAssignment?.vendorAccount?.scheduleOn;

      content = (
        <OverflowAwareText
          sx={(theme) => ({
            color: isVendorAvailable
              ? theme.brandV2.colors.treadBlue2
              : theme.brandV2.colors.treadBlack,
            fontWeight: 600,
            width: 'auto',
          })}
        >
          {vendorJobAssignment?.vendorAccount?.name}
        </OverflowAwareText>
      );
    }
  }

  return <Box>{content}</Box>;
}

interface TruckAndTrailerProps {
  job: JobInvoicing;
}

function TruckAndTrailer({ job }: TruckAndTrailerProps) {
  const { equipment, additionalEquipment } = job;
  const equipmentName = equipment ? getEquipmentLabel(equipment) : null;
  const additionalEquipmentName = additionalEquipment
    ? getEquipmentLabel(additionalEquipment)
    : null;

  return (
    <Box>
      {(equipmentName || additionalEquipmentName) && (
        <FlexColumn sx={{ gap: 0, fontSize: '11px' }}>
          {equipmentName && (
            <OverflowAwareText sx={{ lineHeight: '16px', height: '16px' }}>
              {equipmentName}
            </OverflowAwareText>
          )}

          {additionalEquipmentName && (
            <OverflowAwareText sx={{ lineHeight: '16px', height: '16px' }}>
              {additionalEquipmentName}
            </OverflowAwareText>
          )}
        </FlexColumn>
      )}
    </Box>
  );
}
